import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { PRODUCTS } from "@/data/products";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import ClientsMarquee from "@/components/ClientsMarquee";

const HERO_IMG = "/hero-empanadas.jpg";
const LEGACY_IMG = "/argentina-landscape.jpg";
const PROD_BG = "/cta-produccion.jpg";

const Inicio = () => {
  const { t, lang } = useLang();
  const bestsellers = PRODUCTS.slice(0, 2);

  return (
    <div data-testid="page-inicio">
      {/* HERO — full-bleed image with right-aligned overlay text */}
      <section className="relative">
        {/* Full width image */}
        <div className="relative w-full h-[70vh] md:h-[85vh] lg:h-[88vh] min-h-[520px] overflow-hidden">
          <img
            src={HERO_IMG}
            alt="Empanadas argentinas artesanales"
            className="absolute inset-0 w-full h-full object-cover animate-slow-zoom"
          />
          {/* Dark gradient from left to right to make right-side text legible */}
          <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-ink/40 to-transparent lg:from-ink/30 lg:via-transparent lg:to-ink/70" />

          {/* Right-aligned overlay text + CTAs */}
          <div className="absolute inset-0 flex items-center">
            <div className="w-full max-w-[1500px] mx-auto px-6 md:px-12 lg:px-20">
              <div className="ml-auto max-w-xl lg:max-w-2xl text-right animate-fade-up">
                <div className="text-sm md:text-base lg:text-lg uppercase tracking-[0.3em] text-bg mb-6 md:mb-8 font-medium drop-shadow">
                  {t.home.overline}
                </div>
                <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-light tracking-tighter leading-[0.95] text-bg whitespace-pre-line drop-shadow-sm">
                  {t.home.title}
                </h1>
                <p className="mt-8 md:mt-10 text-base md:text-lg lg:text-xl text-bg/90 font-light leading-relaxed ml-auto">
                  {t.home.sub}
                </p>
                <div className="mt-10 md:mt-12 flex flex-col sm:flex-row gap-4 sm:justify-end">
                  <Link
                    to="/productos"
                    data-testid="hero-cta-primary"
                    className="inline-flex items-center justify-center gap-3 bg-wine text-bg px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-wine-hover transition-colors group"
                  >
                    {t.home.ctaPrimary}
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
                  </Link>
                  <Link
                    to="/produccion"
                    data-testid="hero-cta-secondary"
                    className="inline-flex items-center justify-center gap-3 bg-bg text-ink border border-bg px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-ink hover:text-bg hover:border-ink transition-colors"
                  >
                    {t.home.ctaSecondary}
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Subtle brand mark removed per design */}
        </div>

        {/* Stats row */}
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 lg:px-20 py-20 md:py-28">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 border-t border-bone pt-12">
            {t.home.stats.map((s, i) => (
              <div key={i} data-testid={`stat-${i}`} className="text-left">
                <div className="font-serif text-5xl md:text-6xl text-wine font-light leading-none">
                  {s.v}
                </div>
                <div className="mt-3 text-[11px] uppercase tracking-[0.2em] text-ink-muted">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-wine text-bg py-10 overflow-hidden border-t border-b border-wine-hover">
        <div className="flex whitespace-nowrap animate-marquee">
          {[...Array(2)].map((_, k) => (
            <div key={k} className="flex">
              {t.home.marquee.split("·").map((part, i) => (
                <span
                  key={`${k}-${i}`}
                  className="font-serif italic text-3xl md:text-5xl px-8 flex items-center"
                >
                  {part.trim()}
                  <span className="ml-8 text-caramel">·</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* LEGACY BENTO */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-20">
        <div className="max-w-[1500px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
            <div className="lg:col-span-7 bg-surface p-10 md:p-16 flex flex-col justify-between min-h-[400px] md:min-h-[520px]">
              <div className="text-[11px] uppercase tracking-[0.3em] text-caramel font-medium">
                {t.home.legacyOverline}
              </div>
              <div>
                <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-ink font-light tracking-tight leading-tight">
                  {t.home.legacyTitle}
                </h2>
                <p className="mt-8 text-ink-muted font-light text-base md:text-lg max-w-xl leading-relaxed">
                  {t.home.legacyBody}
                </p>
              </div>
            </div>
            <div className="lg:col-span-5 relative overflow-hidden min-h-[300px] md:min-h-[520px] group">
              <img
                src={LEGACY_IMG}
                alt=""
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CLIENTS MARQUEE */}
      <ClientsMarquee />

      {/* BESTSELLERS */}
      <section className="py-20 md:py-28 px-6 md:px-12 lg:px-20 bg-surface/50">
        <div className="max-w-[1500px] mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-caramel mb-4 font-medium">
                ⟶ {t.home.bestsellersTitle}
              </div>
              <h3 className="font-serif text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-xl">
                {t.home.bestsellersSub}
              </h3>
            </div>
            <Link
              to="/productos"
              data-testid="bestsellers-view-all"
              className="text-[11px] uppercase tracking-[0.2em] text-wine font-medium hover:opacity-70 transition-opacity inline-flex items-center gap-2 self-start md:self-end"
            >
              {t.common.viewAll} <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
            {bestsellers.map((p, idx) => (
              <Link
                key={p.id}
                to="/tienda"
                data-testid={`bestseller-${p.id}`}
                className="group block"
              >
                <div className="relative aspect-[4/5] overflow-hidden bg-surface">
                  <img
                    src={p.image}
                    alt={p.name[lang]}
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="mt-6 flex items-end justify-between gap-4">
                  <div>
                    <h4 className="font-serif text-2xl md:text-3xl text-ink font-light tracking-tight">
                      {p.name[lang] || p.name.es}
                    </h4>
                  </div>
                  <ArrowUpRight
                    className="w-6 h-6 text-wine flex-shrink-0 transition-transform group-hover:rotate-45"
                    strokeWidth={1.5}
                  />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PRODUCTION CTA — parallax */}
      <section className="relative h-[60vh] md:h-[80vh] overflow-hidden flex items-center justify-center">
        <img
          src={PROD_BG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-ink/60" />
        <div className="relative z-10 text-center text-bg px-6 max-w-3xl">
          <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light leading-tight tracking-tight">
            {t.home.ctaProductionTitle}
          </h2>
          <p className="mt-6 text-bone/90 font-light text-base md:text-lg max-w-xl mx-auto">
            {t.home.ctaProductionBody}
          </p>
          <Link
            to="/produccion"
            data-testid="production-cta"
            className="mt-10 inline-flex items-center gap-3 bg-bg text-ink px-8 py-4 text-xs uppercase tracking-[0.2em] font-medium hover:bg-caramel hover:text-bg transition-colors group"
          >
            {t.home.ctaProductionBtn}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Inicio;
